/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const StyleMarqueAngle = require('../types/StyleMarqueAngle')
const Vect = require('../types/Vect')
const kernel = require('../kernel/kernel')
const CCercle = require('./CCercle')
const CMarqueSegment = require('./CMarqueSegment')
const CSegment = require('./CSegment')
const CPointBase = require('./CPointBase')
module.exports = CMarqueAngleAncetre

/**
 * Classe ancêtre de toutes les marques d'angle.
 * @constructor
 * @extends CCercle
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si élément final de construction.
 * @param {mtg32.Color} couleur : La couleur.
 * @param {Boolean} masque : true si l'objet est masqué.
 * @param {mtg32.StleTrait} style : Donne le style trait utilisé.
 * @param {StyleMarqueAngle} styleMarque : Donne le style de la marque.
 * @param {Float} rayon : Le rayon de l'arc.
 * @returns {CMarqueAngleAncetre}
 */
function CMarqueAngleAncetre (listeProprietaire, impProto, estElementFinal, couleur, masque,
    style, styleMarque, rayon) {
  if (arguments.length === 0) return; // Ajout version 4.9.9.4
  if (arguments.length === 1) CCercle.call(this, listeProprietaire);
  else {
    CCercle.call(this, listeProprietaire, impProto, estElementFinal, couleur, masque, style);
    this.styleMarque = styleMarque;
    this.rayon = rayon;
  }
  this.u = new Vect();
  this.v = new Vect();
  this.point1 = new CPointBase();
  this.point2 = new CPointBase();
  this.pointc = new CPointBase();
  this.segment1 = new CSegment(this.point2, this.pointc);
  this.marqueSegment = new CMarqueSegment(listeProprietaire, null, false, null, false, style,
      StyleMarqueAngle.styleMarqueSegment(styleMarque), this.segment1);
}
CMarqueAngleAncetre.prototype = new CCercle();
CMarqueAngleAncetre.prototype.constructor = CMarqueAngleAncetre;
CMarqueAngleAncetre.prototype.superClass = "CCercle";
CMarqueAngleAncetre.prototype.className = "CMarqueAngleAncetre";

CMarqueAngleAncetre.prototype.numeroVersion = function() {
  return 2;
};

CMarqueAngleAncetre.prototype.setClone = function(ptel) {
  CCercle.prototype.setClone.call(this, ptel);
  ptel.u.setCopy(this.u);
  ptel.v.setCopy(this.v);
  this.point1.placeEn(ptel.point1);
  this.point2.placeEn(ptel.point2);
  this.pointc.placeEn(ptel.pointc);
  this.ang1rad = ptel.ang1rad;
  this.ang2rad = ptel.ang2rad;
};
CMarqueAngleAncetre.prototype.getNature = function() {
  return NatObj.NMarqueAngle;
};
/**
 * Utilisé lorsque l'angle est droit.
 * @returns {undefined}
 */
CMarqueAngleAncetre.prototype.prepareAngleDroit = function(){
  var normeu,normev,u1,v1;
  u1 = new Vect();
  v1 = new Vect();
  normeu = this.u.norme();
  normev = this.v.norme();
  u1.x = this.u.x/normeu*this.rayon;
  u1.y = this.u.y/normeu*this.rayon;
  v1.x = this.v.x/normev*this.rayon;
  v1.y = this.v.y/normev*this.rayon;
  this.point1.x = this.centre_x + v1.x;
  this.point1.y = this.centre_y + v1.y;
  this.pointc.x = this.point1.x + u1.x;
  this.pointc.y = this.point1.y + u1.y;
  this.point2.x = this.centre_x + u1.x;
  this.point2.y = this.centre_y + u1.y;
};
/**
 * Renvoie le svg element qui représente la marque d'angle quand l'angle est droit.
 * @param {Float} transparence : La transparence utilisée quand la marque d'anglee est remplie.
 * @returns {svgElement}
 */
CMarqueAngleAncetre.prototype.creategAngleDroit= function(transparence){
  var g,line,pol,points;
  g = kernel.cens("g",{
    "pointer-events" : "none"
  });
  var style = "";
  var stroke = this.style.stroke;
  if (stroke.length !== 0) style += "stroke-dasharray:"+stroke+";";
  var strokewidth = this.style.strokeWidth;
  style += "stroke-width:"+strokewidth+";";
  // style += "stroke:"+this.couleur.rgb()+";"; Modifié version 4.9.9.4
  style += "stroke:"+this.color+";";
  line = kernel.cens("line",{
    x1 : this.point1.x,
    y1 : this.point1.y,
    x2 : this.pointc.x,
    y2 : this.pointc.y,
    style :  style
  });
  g.appendChild(line);
  line = kernel.cens("line",{
    x1 : this.point2.x,
    y1 : this.point2.y,
    x2 : this.pointc.x,
    y2 : this.pointc.y,
    style :  style
  });
  g.appendChild(line);
  if ((this.styleMarque & StyleMarqueAngle.marqueRemplie) !== 0) {
    points = this.centre_x + "," + this.centre_y+" "+ this.point1.x +","+this.point1.y+" "+
      this.pointc.x+","+this.pointc.y+" "+this.point2.x+" "+this.point2.y;
    style = "stroke:none;";
    style += "fill-opacity:"+transparence+";";
    // style += "fill:"+this.couleur.rgb()+";"; Modifié version 4.9.9.4
    style += "fill:"+this.color+";";
    pol = kernel.cens("polygon",{
      style : style,
      points : points
    });
    g.appendChild(pol);
  }
  return g;
};
/**
 * Renvoie le svg element qui représente la marque d'angle quand l'angle n'est pas droit.
 * @param {Float} xdeb : Abscisse du point de départ de l'angle.
 * @param {Float} ydeb : Ordonnée du point de départ de l'angle.
 * @param {Float} xcentre : Abscisse du centre de l'arc.
 * @param {Float} ycentre : Ordonnée du centre de l'arc.
 * @param {Float} ang : L'angle au centre.
 * @returns {svgElement}
 */
CMarqueAngleAncetre.prototype.creategMarqueAngle= function(xdeb, ydeb, xcentre, ycentre, ang){
  var g,line,xmilieu,ymilieu,xc1,yc1,ang2,nvec,demilong;
  g = kernel.cens("g");
  var style = "";
  var stroke = this.style.stroke;
  if (stroke.length !== 0) style += "stroke-dasharray:"+stroke+";";
  var strokewidth = this.style.strokeWidth;
  style += "stroke-width:"+strokewidth+";";
  // style += "stroke:"+this.couleur.rgb()+";"; Modifié version 4.9.9.4
  style += "stroke:"+this.color+";";
  var vec1 = new Vect(xcentre, ycentre, xdeb, ydeb);
  var vec = new Vect();
  var vect2 = new Vect();
  vec1.tourne(ang/2, vec);
  nvec = vec.norme();
  vec.x = vec.x/nvec;
  vec.y = vec.y/nvec;
  // Modification version 5.0 pour tenir compte des figures haute résolution
  // demilong = 5;
  demilong = 5;
  switch (this.styleMarque) {
  case StyleMarqueAngle.marqueSimple1Trait :
  case StyleMarqueAngle.marquePleine1Trait :
    xmilieu = this.centre_x + vec.x*this.rayon;
    ymilieu = this.centre_y + vec.y*this.rayon;
    line = kernel.cens("line",{
      x1 : xmilieu - vec.x*demilong,
      y1 : ymilieu - vec.y*demilong,
      x2 : xmilieu + vec.x*demilong,
      y2 : ymilieu + vec.y*demilong,
      style : style
    });
    g.appendChild(line);
    break;
  case StyleMarqueAngle.marqueSimple2Traits :
  case StyleMarqueAngle.marquePleine2Traits :
    ang2 = Math.atan(3/this.rayon); // Environ 10 pixels entre les deux traits
    vec.tourne(ang2, vect2);
    xc1 = this.centre_x + vect2.x*this.rayon;
    yc1 = this.centre_y + vect2.y*this.rayon;
    line = kernel.cens("line",{
      x1 : xc1 - vec.x*demilong,
      y1 : yc1 - vec.y*demilong,
      x2 : xc1 + vec.x*demilong,
      y2 : yc1 + vec.y*demilong,
      style :  style
    });
    g.appendChild(line);
    vec.tourne(- ang2, vect2);
    xc1 = this.centre_x + vect2.x*this.rayon;
    yc1 = this.centre_y + vect2.y*this.rayon;
    line = kernel.cens("line",{
      x1 : xc1 - vec.x*demilong,
      y1 : yc1 - vec.y*demilong,
      x2 : xc1 + vec.x*demilong,
      y2 : yc1 + vec.y*demilong,
      style : style
    });
    g.appendChild(line);
    break;
  case StyleMarqueAngle.marqueSimple3Traits :
  case StyleMarqueAngle.marquePleine3Traits :
    xmilieu = this.centre_x + vec.x*this.rayon;
    ymilieu = this.centre_y + vec.y*this.rayon;
    line = kernel.cens("line",{
      x1 : xmilieu - vec.x*demilong,
      y1 : ymilieu - vec.y*demilong,
      x2 : xmilieu + vec.x*demilong,
      y2 : ymilieu + vec.y*demilong,
      style : style
    });
    g.appendChild(line);
    ang2 = Math.atan(5/this.rayon); // Environ 5 pixels entre les deux traits
    vec.tourne(ang2, vect2);
    xc1 = this.centre_x + vect2.x*this.rayon;
    yc1 = this.centre_y + vect2.y*this.rayon;
    line = kernel.cens("line",{
      x1 : xc1 - vec.x*demilong,
      y1 : yc1 - vec.y*demilong,
      x2 : xc1 + vec.x*demilong,
      y2 : yc1 + vec.y*demilong,
      style : style
    });
    g.appendChild(line);
    vec.tourne(- ang2, vect2);
    xc1 = this.centre_x + vect2.x*this.rayon;
    yc1 = this.centre_y + vect2.y*this.rayon;
    line = kernel.cens("line",{
      x1 : xc1 - vec.x*demilong,
      y1 : yc1 - vec.y*demilong,
      x2 : xc1 + vec.x*demilong,
      y2 : yc1 + vec.y*demilong,
      style : style
    });
    g.appendChild(line);
    break;
  case StyleMarqueAngle.marqueSimpleCroix :
  case StyleMarqueAngle.marquePleineCroix :
    xmilieu = this.centre_x + vec.x*this.rayon;
    ymilieu = this.centre_y + vec.y*this.rayon;
    vec.tourne(Math.PI/4, vect2);
    line = kernel.cens("line",{
      x1 : xmilieu - vect2.x*demilong,
      y1 : ymilieu - vect2.y*demilong,
      x2 : xmilieu + vect2.x*demilong,
      y2 : ymilieu + vect2.y*demilong,
      style : style
    });
    g.appendChild(line);
    vec.tourne(-Math.PI/4, vect2);
    line = kernel.cens("line",{
      x1 : xmilieu - vect2.x*demilong,
      y1 : ymilieu - vect2.y*demilong,
      x2 : xmilieu + vect2.x*demilong,
      y2 : ymilieu + vect2.y*demilong,
      style : style
    });
    g.appendChild(line);
  }
  g.setAttribute("pointer-events", "none");
  return g;
};
CMarqueAngleAncetre.prototype.createg = function(svg,couleurFond,transparence){
  var g,p,path,p2,path2,style, stroke;
  var n = this.className;
  var skipRightAngle = ((n === "CMarqueAngleOrienteDirecte") && this.angleDroit && (this.angle < 0)) ||
    ((n === "CMarqueAngleOrienteIndirecte") && this.angleDroit && (this.angle > 0));
  if (this.angleDroit && !skipRightAngle) return CMarqueAngleAncetre.prototype.creategAngleDroit.call(this, transparence);
  var mesureAngle = this.anglePourArc();
  g = kernel.cens("g",{
    "pointer-events" : "none"
  });
  p =this.path(mesureAngle);
  style = "";
  stroke = this.style.stroke;
  if (stroke.length !== 0) style += "stroke-dasharray:"+stroke+";";
  var strokewidth = this.style.strokeWidth;
  style += "stroke-width:"+strokewidth+";";
  // style += "stroke:"+this.couleur.rgb()+";fill:none"; Modifié version 4.9.9.4
  style += "stroke:"+this.color+";fill:none";
  path = kernel.cens("path",{
    d : p,
    style : style
  });
  g.appendChild(path);
  p2 = p+"L"+this.o.x+","+this.o.y+"Z";
  style = "stroke:none;";
  if ((this.styleMarque & StyleMarqueAngle.marqueRemplie) !== 0) {
      style += "fill-opacity:"+transparence+";";
      // style += "fill:"+this.couleur.rgb()+";"; Modifié version 4.9.9.4
      style += "fill:"+this.color+";";
  }
  else style += "fill:none";
  path2 = kernel.cens("path",{
    d : p2,
    style : style
  });
  g.appendChild(path2);
  if ((this.styleMarque & StyleMarqueAngle.marqueAvecTrait) !== 0) {
    g.appendChild(this.creategMarqueAngle(this.a.x, this.a.y, this.o.x, this.o.y, mesureAngle));
  }
  return g;
};
/**
 * Renvoie l'angle au centre pour tracer l'arc.
 * @returns {Float}
 */
CMarqueAngleAncetre.prototype.anglePourArc = function() {
  var mes = this.u.mesureAngleVecteurs(this.v);
  if (kernel.zeroAngle(Math.PI - Math.abs(mes))) mes = Math.PI;
  return mes;
};
CMarqueAngleAncetre.prototype.chaineDesignation = function() {
  return "desMarqueAngle";
};
CMarqueAngleAncetre.prototype.read = function(inps, list){
  CCercle.prototype.read.call(this, inps, list);
  this.styleMarque = inps.readInt();
  this.rayon = inps.readDouble();
};
CMarqueAngleAncetre.prototype.write = function(oups, list){
  CCercle.prototype.write.call(this, oups, list);
  oups.writeInt(this.styleMarque);
  oups.writeDouble(this.rayon);
};



// WEBPACK FOOTER //
// ./src/objets/CMarqueAngleAncetre.js